import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { config } from 'src/config'
import { APP_NAME, useSession } from './auth.provider'

type TSocketContext = { socket?: Socket; isSocketLoading?: boolean; socketError?: Error | string | null }

export const SocketContext = createContext<TSocketContext>({
  socket: undefined,
  isSocketLoading: true,
  socketError: null,
})

const useSocket = () => useContext(SocketContext)
export { useSocket }

const RECONNECTION_DELAY_MS = 1000
const RECONNECTION_DELAY_MAX_MS = 5000

function SocketProvider({ children }: { children: React.ReactNode }) {
  const [socket, setSocket] = useState<Socket | undefined>(undefined)
  const [isSocketLoading, setIsSocketLoading] = useState(true)
  const [socketError, setSocketError] = useState<Error | string | null>(null)
  const socketRef = useRef<Socket | null>(null)
  const { accessToken, refreshSession } = useSession()
  // Always holds the latest accessToken so the auth callback sends a fresh token on every reconnect attempt
  const accessTokenRef = useRef(accessToken)

  // Keep the ref in sync on every render so the auth callback always has the latest token
  accessTokenRef.current = accessToken

  useEffect(() => {
    if (!accessToken) {
      setIsSocketLoading(false)
      return undefined
    }

    setIsSocketLoading(true)

    const socketUrl = config.API_WS
    if (!socketUrl) {
      console.warn('API_WS is not defined, socket connection disabled')
      setIsSocketLoading(false)
      return
    }

    const socketOptions = {
      transports: ['websocket'],
      path: '/v2/ws',
      // Callback form: called on every connection attempt so reconnects always send the latest token,
      // even if it was refreshed while the socket was in its retry loop.
      auth: (cb: (data: { accessToken: string | undefined }) => void) => {
        cb({ accessToken: accessTokenRef.current })
      },
      headers: {
        'x-client-app': APP_NAME,
      },
      withCredentials: true,
      reconnection: true,
      reconnectionDelay: RECONNECTION_DELAY_MS,
      reconnectionDelayMax: RECONNECTION_DELAY_MAX_MS,
    }
    const localSocket = io(socketUrl, socketOptions)
    socketRef.current = localSocket

    // Whether we have had at least one successful connection on this socket instance
    let hasEverConnected = false

    localSocket.on('connect', () => {
      setIsSocketLoading(false)
      setSocketError(null)
      setSocket(localSocket)
      hasEverConnected = true
    })

    localSocket.on('disconnect', (reason) => {
      setSocket(undefined)
      setSocketError(reason)
      // Socket.IO won't auto-reconnect after a server-initiated disconnect
      if (reason === 'io server disconnect') {
        localSocket.connect()
      }
    })

    localSocket.on('connect_error', (data) => {
      console.warn('Socket connection failed:', data)
      setSocketError(data)
      setIsSocketLoading(false)

      // After a prior successful connection (e.g. backend reboot, computer waking from sleep),
      // the JWT may have expired while the socket was retrying. Proactively refresh the session
      // so the auth callback picks up a fresh token on the next attempt.
      // getSessionData() is a no-op when the token is still valid, so this is safe to call always.
      if (hasEverConnected) {
        refreshSession?.()
      }
    })

    return () => {
      localSocket.close()
      socketRef.current = null
    }
  }, [accessToken, refreshSession])

  const value = useMemo(
    () => ({ socket, isSocketLoading, socketError }),
    [socket, isSocketLoading, socketError],
  )

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
}

export default SocketProvider
